import { useEffect, useState } from 'react';
import { Users, UserPlus, Clock, CheckCircle, Gift } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '@core/contexts/ProfileContext';
import { useIdentity } from '@core/contexts/IdentityContext';
import { getMyFriends, type InvitedFriend, type InvitedByInfo } from '@core/api/users';

export function MyFriendsList() {
  // Друзья — по сессии входа (K-002), в обоих входах; Telegram ID — необязательная сверка (только мини-апп).
  const { user: profileUser, loading: profileLoading } = useProfile();
  const { telegramId } = useIdentity();
  const userId = profileUser?.id ?? null;
  const [friends, setFriends] = useState<InvitedFriend[]>([]);
  const [totalFriends, setTotalFriends] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [invitedBy, setInvitedBy] = useState<InvitedByInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      if (!profileLoading) setLoading(false);
      return;
    }

    const loadFriends = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getMyFriends(telegramId);
        setFriends(data.friends);
        setTotalFriends(data.totalFriends);
        setTotalEarned(data.totalEarned);
        setInvitedBy(data.invitedBy);
      } catch (err) {
        console.error('Failed to load friends:', err);
        setError('Не удалось загрузить список друзей');
      } finally {
        setLoading(false);
      }
    };

    loadFriends();
  }, [userId, telegramId, profileLoading]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    }
  };

  if (loading) {
    return (
      <div className="mt-8 space-y-4">
        <div className="flex items-center gap-2 text-text-muted">
          <Users size={20} />
          <span className="font-semibold">Мои друзья</span>
        </div>
        <div className="bg-card rounded-brand p-6 border border-border">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-secondary rounded w-1/3" />
            <div className="h-12 bg-secondary rounded" />
            <div className="h-12 bg-secondary rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8">
        <div className="bg-card rounded-brand p-4 border border-error/30 text-center">
          <p className="text-sm text-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="mt-8 space-y-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Section Header */}
      <motion.div variants={itemVariants} className="flex items-center gap-2 text-text-main">
        <Users size={20} className="text-primary" />
        <span className="font-semibold">Мои друзья</span>
      </motion.div>

      {/* Stats Card */}
      <motion.div
        variants={itemVariants}
        className="bg-card rounded-brand p-5 border border-border"
      >
        <div className="flex justify-between items-center">
          <div className="text-center flex-1">
            <p className="text-xs text-text-muted mb-1">Приглашено</p>
            <p className="text-2xl font-bold text-primary">{totalFriends}</p>
          </div>
          <div className="w-px h-10 bg-border" />
          <div className="text-center flex-1">
            <p className="text-xs text-text-muted mb-1">Заработано</p>
            <p className="text-2xl font-bold text-success">+{totalEarned}</p>
          </div>
        </div>
      </motion.div>

      {/* Friends List */}
      <AnimatePresence mode="wait">
        {friends.length === 0 ? (
          <motion.div
            key="empty"
            variants={itemVariants}
            className="bg-card rounded-brand p-6 border border-border text-center"
          >
            <UserPlus size={40} className="mx-auto mb-3 text-text-muted opacity-40" />
            <p className="text-text-muted text-sm">Пока нет приглашённых друзей</p>
            <p className="text-text-muted text-xs mt-1 opacity-70">
              Поделись ссылкой и получай бонусы!
            </p>
          </motion.div>
        ) : (
          <motion.div key="list" className="space-y-2">
            {friends.map((friend, index) => (
              <motion.div
                key={friend.id}
                variants={itemVariants}
                custom={index}
                className="bg-card rounded-brand p-4 border border-border flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    friend.hasMadePurchase ? 'bg-success/20' : 'bg-secondary'
                  }`}>
                    {friend.hasMadePurchase ? (
                      <CheckCircle size={20} className="text-success" />
                    ) : (
                      <Clock size={20} className="text-text-muted" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-text-main">
                      {friend.firstName || 'Друг'} {friend.lastName || ''}
                    </p>
                    <p className="text-xs text-text-muted">
                      {formatDate(friend.joinedAt)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {friend.hasMadePurchase ? (
                    <p className="font-bold text-success">+{friend.bonusEarned}</p>
                  ) : (
                    <p className="text-xs text-text-muted">Ждём покупку</p>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invited By Section */}
      {invitedBy && (
        <motion.div
          variants={itemVariants}
          className="bg-card/50 rounded-brand p-4 border border-border/50"
        >
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              invitedBy.status === 'received' ? 'bg-success/20' : 'bg-secondary'
            }`}>
              <Gift size={16} className={
                invitedBy.status === 'received' ? 'text-success' : 'text-text-muted'
              } />
            </div>
            <div className="flex-1">
              <p className="text-sm text-text-muted">
                {invitedBy.name ? `Тебя пригласил: ${invitedBy.name}` : 'Тебя пригласили'}
              </p>
              <p className="text-xs text-text-muted opacity-70">
                {invitedBy.status === 'received'
                  ? `Ты получил +${invitedBy.bonusReceived} за регистрацию`
                  : 'Бонус начислится после первой покупки'
                }
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
